import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import ListingCard from '../components/ListingCard';
import AddListingModal from '../components/AddListingModal';
import EditListingModal from '../components/EditListingModal';
import Pagination from '../components/Pagination';

const LISTINGS_PER_PAGE = 12;

function Neplatiči({ isAdmin, onLogout }) {
  const [allListings, setAllListings] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [listingToEdit, setListingToEdit] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  const { pageNumber } = useParams();
  const navigate = useNavigate();
  const currentPage = pageNumber ? parseInt(pageNumber, 10) : 1;
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Universal scroll position restoration
  useEffect(() => {
    const isReturningFromListing = sessionStorage.getItem('lastListingPage') === '/neplatici' || 
                                   sessionStorage.getItem('lastListingPage')?.startsWith('/neplatici/');
    
    if (isReturningFromListing) {
      console.log('🔄 Detected return to neplatici page from listing detail');
      
      // Clear the return flag
      sessionStorage.removeItem('lastListingPage');
      
      // Restore scroll position after content loads
      const scrollTimeout = setTimeout(() => {
        const savedScrollPosition = sessionStorage.getItem('scrollPosition-/neplatici') || 
                                   sessionStorage.getItem(`scrollPosition-/neplatici/${currentPage}`);
        
        if (savedScrollPosition) {
          const scrollY = parseInt(savedScrollPosition, 10);
          console.log(`📍 Restoring scroll position to: ${scrollY}px`);
          
          // Use requestAnimationFrame to ensure DOM is ready
          requestAnimationFrame(() => {
            window.scrollTo({
              top: scrollY,
              behavior: 'auto'
            });
          });
          
          // Clean up the scroll position after restoring
          sessionStorage.removeItem('scrollPosition-/neplatici');
          sessionStorage.removeItem(`scrollPosition-/neplatici/${currentPage}`);
        }
      }, 100);
      
      return () => clearTimeout(scrollTimeout);
    } else {
      // Normal page navigation - scroll to top
      window.scrollTo(0, 0);
    }
  }, [currentPage, allListings.length]);

  // Scroll to top when page changes (but not when returning from listing)
  useEffect(() => {
    const isReturningFromListing = sessionStorage.getItem('lastListingPage') === '/neplatici' || 
                                   sessionStorage.getItem('lastListingPage')?.startsWith('/neplatici/');
    
    if (!isReturningFromListing) {
      window.scrollTo(0, 0);
    }
  }, [currentPage]);
  
  // Update window width when resized
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Determine number of columns based on window width
  const getGridColumns = () => {
    if (windowWidth <= 600) return '1fr'; // Mobile: 1 column
    if (windowWidth <= 900) return 'repeat(2, 1fr)'; // Tablet: 2 columns
    if (windowWidth <= 1200) return 'repeat(3, 1fr)'; // Small desktop: 3 columns
    return 'repeat(4, 1fr)'; // Large desktop: 4 columns
  };

  useEffect(() => {
    // Fetch neplatiči listings
    const fetchNeplatici = async () => {
    try {
        setIsLoading(true);
        const res = await fetch('https://eurocentrumv1.onrender.com/listings');
        const data = await res.json();
        
        // Filter to only include listings with "neplatiči" tag
        const filteredData = data.filter(listing => {
          // Check if listing has tags
          if (!listing.tags || !Array.isArray(listing.tags)) {
            return false;
          }
          
          // Include only if it has neplatiči tag
          return listing.tags.includes('neplatiči');
        });
        
        // Set the filtered data
        setAllListings(filteredData);
      } catch (error) {
        console.error('Error fetching neplatiči listings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNeplatici();
  }, []);

  const refreshAll = async () => {
    try {
      setIsLoading(true);
      const res = await fetch('https://eurocentrumv1.onrender.com/listings');
      const data = await res.json();
      
      // Filter to only include listings with "neplatiči" tag
      const filteredData = data.filter(listing => {
        // Check if listing has tags
        if (!listing.tags || !Array.isArray(listing.tags)) {
          return false;
        }
        
        // Include only if it has neplatiči tag
        return listing.tags.includes('neplatiči');
      });
      
      // Set all listings
      setAllListings(filteredData);
    } catch (error) {
      console.error('Error refreshing neplatiči listings', error);
    } finally {
        setIsLoading(false);
    }
  };

  // Add new listing at the top of the list
  const handleAddListing = (newListing) => {
    console.log("Adding new listing to Neplatici:", newListing);
    refreshAll();
    setShowAddModal(false);
  };

  const handleEditClick = (listing) => {
    setListingToEdit(listing);
    setShowEditModal(true);
  };

  // Enhanced edit modal close handler that preserves scroll position
  const handleEditModalClose = () => {
    console.log('🔄 Admin finished editing listing on Neplatici - preserving scroll position');
    
    // Save current scroll position before closing modal
    const currentPath = window.location.pathname;
    const scrollY = window.scrollY || window.pageYOffset;
    sessionStorage.setItem(`scrollPosition-${currentPath}`, scrollY.toString());
    
    // Set flag to indicate we should restore position after refresh
    sessionStorage.setItem('preserveScrollAfterEdit', 'true');
    
    console.log(`💾 Saved scroll position for admin edit: ${scrollY}px`);
    
    setShowEditModal(false);
    setListingToEdit(null);
  };

  // Enhanced refresh function that restores scroll position after admin edits
  const handleRefreshAfterEdit = async () => {
    await refreshAll();
    
    // Check if we need to restore scroll position after edit
    const shouldPreserveScroll = sessionStorage.getItem('preserveScrollAfterEdit');
    
    if (shouldPreserveScroll) {
      console.log('🔄 Restoring scroll position after admin edit on Neplatici');
      
      // Remove the flag
      sessionStorage.removeItem('preserveScrollAfterEdit');
      
      // Restore scroll position after content loads
      setTimeout(() => {
        const currentPath = window.location.pathname;
        const savedScrollPosition = sessionStorage.getItem(`scrollPosition-${currentPath}`);
        
        if (savedScrollPosition) {
          const scrollY = parseInt(savedScrollPosition, 10);
          console.log(`📍 Restoring scroll position after edit: ${scrollY}px`);
          
          window.scrollTo({
            top: scrollY,
            behavior: 'auto'
          });
          
          // Clean up
          sessionStorage.removeItem(`scrollPosition-${currentPath}`);
        }
      }, 100);
    }
  };

  // Handle tag clicks for navigation
  const handleTagClick = (tag) => {
    // Navigate to the appropriate page based on the tag
    if (tag === 'auto') {
      window.location.href = '/auta';
    } else if (tag === 'byt' || tag === 'dom' || tag === 'pozemok' || tag === 'nebytový priestor' || tag === 'priemyselná budova') {
      window.location.href = '/nehnutelnosti';
    }
  };

  // Animation variants
  const itemVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1 },
  };

  // Filter listings based on search query
  const filteredListings = allListings.filter(listing => {
    if (!searchQuery) return true;
    
    const searchLower = searchQuery.toLowerCase();
    return (
      (listing.name && listing.name.toLowerCase().includes(searchLower)) ||
      (listing.location && listing.location.toLowerCase().includes(searchLower)) ||
      (listing.description && listing.description.toLowerCase().includes(searchLower))
    );
  });

  // Calculate pagination for filtered listings
  const totalPages = Math.ceil(filteredListings.length / LISTINGS_PER_PAGE);
  const startIndex = (currentPage - 1) * LISTINGS_PER_PAGE;
  const endIndex = startIndex + LISTINGS_PER_PAGE;
  const displayedListings = filteredListings.slice(startIndex, endIndex);

  // Redirect to page 1 if current page is invalid
  useEffect(() => {
    if (allListings.length > 0 && totalPages > 0 && currentPage > totalPages) {
      navigate('/neplatici');
    }
  }, [currentPage, totalPages, allListings.length, navigate]);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.heading}>STOP dílerom drog, narkomanom a neplatičom</h2>
        {isAdmin && (
          <button 
            style={styles.addButton}
            onClick={() => setShowAddModal(true)}
          >
            + Pridať neplatiča
          </button>
        )}
      </div>
      
      {/* Search Bar */}
      <div style={styles.searchContainer}>
        <input
          type="text"
          placeholder="Hľadať podľa mena, lokácie alebo popisu..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
        />
      </div>
      
      {isLoading ? (
        <div style={styles.loadingContainer}>
          <div style={styles.loadingSpinner}></div>
          <p style={styles.loadingText}>Načítavam zoznam neplatičov...</p>
        </div>
        ) : (
          <>
            <div style={{
              ...styles.listingsContainer,
            display: 'grid',
            gridTemplateColumns: getGridColumns(),
            gap: '20px'
            }}>
            <AnimatePresence>
              {displayedListings.length > 0 ? (
                displayedListings.map((listing) => (
                  <motion.div
                    key={listing._id}
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                    exit={{ opacity: 0 }}
                    layout
                  >
                      <ListingCard
                        listing={listing}
                        isAdmin={isAdmin}
                      onRefresh={refreshAll}
                        onEditClick={handleEditClick}
                      onTagClick={handleTagClick}
                      customCardStyle={styles.neplaticiCard}
                        customImageStyle={styles.customImageStyle}
                      />
                  </motion.div>
                ))
              ) : (
                <div style={styles.emptyMessage}>
                  <h3>Žiadny záznam o neplatičoch</h3>
                  <p>{searchQuery ? 'Žiadne výsledky pre vyhľadávanie.' : 'Momentálne nemáme vedené žiadne záznamy o neplatičoch.'}</p>
                </div>
              )}
              </AnimatePresence>
            </div>
            
            {/* Pagination */}
            <Pagination 
              currentPage={currentPage}
              totalPages={totalPages}
              basePath="/neplatici"
            />

            {/* Add Listing Modal */}
            {showAddModal && isAdmin && (
              <AddListingModal
                onClose={() => setShowAddModal(false)}
                onRefresh={refreshAll}
                onAddListing={handleAddListing}
                initialTags={['neplatiči']}
              />
            )}

            {/* Edit Listing Modal */}
            {showEditModal && isAdmin && (
              <EditListingModal
                listingToEdit={listingToEdit}
                onClose={handleEditModalClose}
                onRefresh={handleRefreshAfterEdit}
              />
            )}
          </>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '80px auto 0',
    padding: '1rem',
    width: '100%',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  heading: {
    fontSize: 'clamp(1.5rem, 4vw, 2rem)',
    margin: 0,
    flex: 1,
    minWidth: '200px',
  },
  addButton: {
    backgroundColor: 'rgb(44, 105, 160)',
    color: '#fff',
    border: 'none',
    padding: '0.7rem 1.2rem',
    cursor: 'pointer',
    fontSize: '1rem',
    borderRadius: '0.5rem',
    fontWeight: '500',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
    },
  },
  listingsContainer: {
    display: 'grid',
    gap: '1rem',
    width: '100%',
    padding: '0',
    margin: '0',
    boxSizing: 'border-box',
  },
  cardWrapper: {
    maxWidth: '100%',
    width: '100%',
    boxSizing: 'border-box',
  },
  // Custom style for reasonably sized images on Neplatici page
  customImageStyle: {
    height: '300px', // Reduced from 450px to prevent overflow
    objectFit: 'cover',
    width: '100%',
  },
  // Ensure cards have correct height and fit properly
  cardStyle: {
    height: 'auto',
    minHeight: '450px', // Reduced from 600px
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '300px',
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #3498db',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  emptyMessage: {
    textAlign: 'center',
    fontSize: '1.2rem',
    color: '#666',
    margin: '2rem 0',
    gridColumn: '1 / -1',
  },
  neplaticiCard: {
    height: 'auto',
    minHeight: '450px', // Reduced from 600px
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  loadingText: {
    marginTop: '1rem',
  },
  searchContainer: {
    marginBottom: '2rem',
    width: '100%',
  },
  searchInput: {
    width: '100%',
    padding: '0.8rem 1rem',
    fontSize: '1rem',
    border: '1px solid #ddd',
    borderRadius: '0.5rem',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    transition: 'all 0.2s ease',
    '&:focus': {
      outline: 'none',
      borderColor: 'rgb(44, 105, 160)',
      boxShadow: '0 2px 8px rgba(44, 105, 160, 0.1)',
    },
  },
};

export default Neplatiči; 